import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  DndContext, PointerSensor, useSensor, useSensors, closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, arrayMove, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  GripVertical, Pencil, Copy, Eye, EyeOff, Trash2, Plus, Type, Image as ImageIcon,
  MousePointerClick, Heading as HeadingIcon,
} from 'lucide-react';
import { galleryItems, pricingCategories as fallbackPricing } from '../../mock';
import BlockRenderer from './BlockRenderer';
import { isSelfContained, BG_PRESETS, SPACING_PRESETS, WIDTH_PRESETS, BLOCK_LABELS, makeSection, makeBlock } from './blockDefs';
import BlockLibrary from './BlockLibrary';
import EditorToolbar from './EditorToolbar';
import PropertiesPanel from './PropertiesPanel';
import { getPageAdmin, savePageDraft, publishPage, resetPage } from './builderApi';

const nid = () => Math.random().toString(36).slice(2, 12);
const clone = (o) => JSON.parse(JSON.stringify(o));
// Re-id a section/block deeply (for duplicate).
const reidBlock = (b) => ({ ...clone(b), id: nid() });
const reidSection = (s) => ({ ...clone(s), id: nid(), blocks: (s.blocks || []).map(reidBlock) });

const DEVICE_WIDTH = { desktop: '100%', tablet: '834px', mobile: '390px' };

export default function PageEditor({ pageKey = 'home', onClose }) {
  const [page, setPage] = useState(null);
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);
  const [selection, setSelection] = useState(null);
  const [device, setDevice] = useState('desktop');
  const [saveState, setSaveState] = useState('Loaded');
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const saveTimer = useRef(null);
  const ctx = useMemo(() => ({ pricingCategories: fallbackPricing, galleryItems }), []);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  useEffect(() => {
    (async () => {
      try {
        const data = await getPageAdmin(pageKey);
        setPage(data.draft || { sections: [] });
      } catch {
        toast.error('Failed to load page');
      }
    })();
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [pageKey]);

  const scheduleAutosave = useCallback((next) => {
    setSaveState('Unsaved changes');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaveState('Saving…');
      try {
        await savePageDraft(pageKey, next.sections);
        setSaveState('Saved');
      } catch {
        setSaveState('Save failed');
      }
    }, 1400);
  }, [pageKey]);

  // Apply a mutation with undo history + autosave.
  const apply = useCallback((mutator) => {
    setPage((cur) => {
      const next = mutator(clone(cur));
      setPast((p) => [...p.slice(-49), cur]);
      setFuture([]);
      scheduleAutosave(next);
      return next;
    });
  }, [scheduleAutosave]);

  const undo = () => {
    setPast((p) => {
      if (!p.length) return p;
      const prev = p[p.length - 1];
      setFuture((f) => [page, ...f]);
      setPage(prev);
      scheduleAutosave(prev);
      return p.slice(0, -1);
    });
  };
  const redo = () => {
    setFuture((f) => {
      if (!f.length) return f;
      const nextPage = f[0];
      setPast((p) => [...p, page]);
      setPage(nextPage);
      scheduleAutosave(nextPage);
      return f.slice(1);
    });
  };

  // ---- Mutations ----
  const addSection = (type) => {
    const sec = makeSection(type);
    apply((pg) => {
      const idx = selection ? pg.sections.findIndex((s) => s.id === selection.sectionId) : -1;
      if (idx >= 0) pg.sections.splice(idx + 1, 0, sec);
      else pg.sections.push(sec);
      return pg;
    });
    setSelection({ kind: 'block', sectionId: sec.id, blockId: sec.blocks[0].id });
    toast.success('Block added');
  };

  const addBlockInside = (sectionId, type) => {
    const blk = makeBlock(type);
    apply((pg) => {
      const s = pg.sections.find((x) => x.id === sectionId);
      if (s) s.blocks.push(blk);
      return pg;
    });
    setSelection({ kind: 'block', sectionId, blockId: blk.id });
  };

  const duplicateSection = (sectionId) => apply((pg) => {
    const idx = pg.sections.findIndex((s) => s.id === sectionId);
    if (idx >= 0) pg.sections.splice(idx + 1, 0, reidSection(pg.sections[idx]));
    return pg;
  });
  const deleteSection = (sectionId) => { apply((pg) => ({ ...pg, sections: pg.sections.filter((s) => s.id !== sectionId) })); setSelection(null); };
  const toggleSection = (sectionId) => apply((pg) => {
    const s = pg.sections.find((x) => x.id === sectionId); if (s) s.visible = s.visible === false; return pg;
  });

  const duplicateBlock = (sectionId, blockId) => apply((pg) => {
    const s = pg.sections.find((x) => x.id === sectionId);
    if (s) { const i = s.blocks.findIndex((b) => b.id === blockId); if (i >= 0) s.blocks.splice(i + 1, 0, reidBlock(s.blocks[i])); }
    return pg;
  });
  const deleteBlock = (sectionId, blockId) => { apply((pg) => {
    const s = pg.sections.find((x) => x.id === sectionId); if (s) s.blocks = s.blocks.filter((b) => b.id !== blockId); return pg;
  }); setSelection(null); };
  const toggleBlock = (sectionId, blockId) => apply((pg) => {
    const s = pg.sections.find((x) => x.id === sectionId); const b = s?.blocks.find((x) => x.id === blockId); if (b) b.visible = b.visible === false; return pg;
  });

  const updateBlockContent = (patch) => apply((pg) => {
    const s = pg.sections.find((x) => x.id === selection.sectionId);
    const b = s?.blocks.find((x) => x.id === selection.blockId);
    if (b) b.content = { ...b.content, ...patch };
    return pg;
  });
  const updateBlockSettings = (patch) => apply((pg) => {
    const s = pg.sections.find((x) => x.id === selection.sectionId);
    const b = s?.blocks.find((x) => x.id === selection.blockId);
    if (b) b.settings = { ...b.settings, ...patch };
    return pg;
  });
  const updateSectionSettings = (patch) => apply((pg) => {
    const s = pg.sections.find((x) => x.id === selection.sectionId);
    if (s) s.settings = { ...s.settings, ...patch };
    return pg;
  });

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const aType = active.data.current?.type;
    const oData = over.data.current || {};
    if (aType === 'section') {
      if (oData.type !== 'section') return;
      apply((pg) => {
        const from = pg.sections.findIndex((s) => s.id === active.id);
        const to = pg.sections.findIndex((s) => s.id === over.id);
        if (from < 0 || to < 0) return pg;
        pg.sections = arrayMove(pg.sections, from, to);
        return pg;
      });
    } else if (aType === 'block') {
      const fromSecId = active.data.current.sectionId;
      apply((pg) => {
        const fromSec = pg.sections.find((s) => s.id === fromSecId);
        if (!fromSec) return pg;
        const bIdx = fromSec.blocks.findIndex((b) => b.id === active.id);
        if (bIdx < 0) return pg;
        const [moved] = fromSec.blocks.splice(bIdx, 1);
        let toSecId; let toIndex;
        if (oData.type === 'block') { toSecId = oData.sectionId; }
        else if (oData.type === 'body' || oData.type === 'section') { toSecId = oData.sectionId || over.id; }
        else { toSecId = fromSecId; }
        const toSec = pg.sections.find((s) => s.id === toSecId) || fromSec;
        if (oData.type === 'block') { toIndex = toSec.blocks.findIndex((b) => b.id === over.id); if (toIndex < 0) toIndex = toSec.blocks.length; }
        else toIndex = toSec.blocks.length;
        toSec.blocks.splice(toIndex, 0, moved);
        return pg;
      });
    }
  };

  const doSaveDraft = async () => {
    setSaving(true);
    try { await savePageDraft(pageKey, page.sections); setSaveState('Saved'); toast.success('Draft saved'); }
    catch { toast.error('Save failed'); } finally { setSaving(false); }
  };
  const doPreview = async () => {
    await savePageDraft(pageKey, page.sections).catch(() => {});
    window.open('/?preview=1', '_blank');
  };
  const doPublish = async () => {
    setPublishing(true);
    try { await savePageDraft(pageKey, page.sections); await publishPage(pageKey); toast.success('Published — your changes are now live'); }
    catch { toast.error('Publish failed'); } finally { setPublishing(false); }
  };
  const doReset = async () => {
    if (!window.confirm('Rebuild the draft from the original homepage content? Unsaved changes will be lost.')) return;
    try { const data = await resetPage(pageKey); setPage(data.draft); setPast([]); setFuture([]); setSelection(null); toast.success('Draft reset to original'); }
    catch { toast.error('Reset failed'); }
  };

  const selSection = page?.sections.find((s) => s.id === selection?.sectionId);
  const selBlock = selSection?.blocks.find((b) => b.id === selection?.blockId);

  if (!page) return <div className="fixed inset-0 z-50 bg-slate-950 flex items-center justify-center text-gray-400">Loading editor…</div>;

  return (
    <div className="fixed inset-0 z-50 bg-slate-950 flex flex-col" data-testid="page-editor">
      <EditorToolbar
        device={device} setDevice={setDevice}
        onUndo={undo} onRedo={redo} canUndo={past.length > 0} canRedo={future.length > 0}
        onSaveDraft={doSaveDraft} onPreview={doPreview} onPublish={doPublish} onClose={onClose} onReset={doReset}
        saveState={saveState} saving={saving} publishing={publishing}
      />
      <div className="flex-1 flex min-h-0">
        {/* Left: block library */}
        <aside className="w-56 shrink-0 bg-slate-900 border-r border-slate-800 overflow-y-auto">
          <BlockLibrary onAdd={addSection} />
        </aside>

        {/* Center: canvas */}
        <main className="flex-1 overflow-y-auto bg-slate-800/40 p-6" data-testid="editor-canvas" onClick={() => setSelection(null)}>
          <div className="mx-auto transition-all duration-300 bg-slate-950 min-h-full shadow-2xl" style={{ maxWidth: DEVICE_WIDTH[device] }}>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={page.sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                {page.sections.map((section) => (
                  <SortableSection
                    key={section.id} section={section} ctx={ctx}
                    selection={selection} setSelection={setSelection}
                    onDup={duplicateSection} onDel={deleteSection} onToggle={toggleSection}
                    onDupBlock={duplicateBlock} onDelBlock={deleteBlock} onToggleBlock={toggleBlock}
                    onAddBlock={addBlockInside}
                  />
                ))}
              </SortableContext>
            </DndContext>
            {page.sections.length === 0 && (
              <div className="text-center text-gray-500 py-24">Add a block from the left to get started.</div>
            )}
          </div>
        </main>

        {/* Right: properties */}
        <aside className="w-80 shrink-0 bg-slate-900 border-l border-slate-800 overflow-y-auto" data-testid="properties-panel">
          <PropertiesPanel
            selection={selection} section={selSection} block={selBlock}
            onBlock={updateBlockContent} onBlockSettings={updateBlockSettings} onSection={updateSectionSettings}
          />
        </aside>
      </div>
    </div>
  );
}

// ---- Sortable section ----
function SortableSection({ section, ctx, selection, setSelection, onDup, onDel, onToggle, onDupBlock, onDelBlock, onToggleBlock, onAddBlock }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id, data: { type: 'section' } });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const selected = selection?.kind === 'section' && selection.sectionId === section.id;
  const hidden = section.visible === false;
  const label = BLOCK_LABELS[section.blocks?.[0]?.type] || 'Section';
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div ref={setNodeRef} style={style} data-testid={`ed-section-${section.id}`}
      className={`relative border-2 ${selected ? 'border-red-500' : 'border-transparent hover:border-slate-600'} ${hidden ? 'opacity-50' : ''}`}>
      {/* Section control bar */}
      <div className="absolute -top-0 left-0 right-0 z-20 flex items-center justify-between bg-slate-900/95 border-b border-slate-700 px-2 py-1">
        <div className="flex items-center gap-2 min-w-0">
          <button {...attributes} {...listeners} data-testid={`ed-section-drag-${section.id}`} className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-white touch-none"><GripVertical className="w-4 h-4" /></button>
          <span className="text-[11px] uppercase tracking-widest text-gray-300 font-semibold truncate">{label}</span>
        </div>
        <div className="flex items-center gap-1">
          <IconBtn testid={`ed-section-edit-${section.id}`} title="Edit section" onClick={(e) => { e.stopPropagation(); setSelection({ kind: 'section', sectionId: section.id }); }}><Pencil className="w-3.5 h-3.5" /></IconBtn>
          <IconBtn testid={`ed-section-dup-${section.id}`} title="Duplicate" onClick={(e) => { e.stopPropagation(); onDup(section.id); }}><Copy className="w-3.5 h-3.5" /></IconBtn>
          <IconBtn testid={`ed-section-hide-${section.id}`} title={hidden ? 'Show' : 'Hide'} onClick={(e) => { e.stopPropagation(); onToggle(section.id); }}>{hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}</IconBtn>
          <IconBtn testid={`ed-section-del-${section.id}`} title="Delete" danger onClick={(e) => { e.stopPropagation(); if (window.confirm('Delete this section?')) onDel(section.id); }}><Trash2 className="w-3.5 h-3.5" /></IconBtn>
        </div>
      </div>

      {/* Blocks */}
      <div className="pt-8">
        <SortableContext items={(section.blocks || []).map((b) => b.id)} strategy={verticalListSortingStrategy}>
          <SectionBody section={section} ctx={ctx} selection={selection} setSelection={setSelection} onDupBlock={onDupBlock} onDelBlock={onDelBlock} onToggleBlock={onToggleBlock} />
        </SortableContext>
      </div>

      {/* Add block inside */}
      <div className="relative z-20 flex justify-center py-2 bg-slate-900/60 border-t border-slate-800">
        <div className="relative">
          <button type="button" data-testid={`ed-addblock-${section.id}`} onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
            className="flex items-center gap-1.5 text-xs text-gray-300 hover:text-white border border-dashed border-slate-600 hover:border-red-500 rounded-full px-4 py-1.5">
            <Plus className="w-3.5 h-3.5" /> Add block here
          </button>
          {menuOpen && (
            <div className="absolute left-1/2 -translate-x-1/2 mt-1 bg-slate-900 border border-slate-700 rounded-md p-1 flex gap-1 z-30 shadow-xl" onClick={(e) => e.stopPropagation()}>
              {[['heading', HeadingIcon], ['text', Type], ['image', ImageIcon], ['button', MousePointerClick]].map(([t, Icon]) => (
                <button key={t} type="button" data-testid={`ed-addblock-${section.id}-${t}`} title={t} onClick={() => { onAddBlock(section.id, t); setMenuOpen(false); }} className="p-2 rounded text-gray-300 hover:bg-slate-800 hover:text-white"><Icon className="w-4 h-4" /></button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Droppable body so blocks can be dropped into empty/other sections.
function SectionBody({ section, ctx, selection, setSelection, onDupBlock, onDelBlock, onToggleBlock }) {
  const { setNodeRef } = useSortable({ id: `body-${section.id}`, data: { type: 'body', sectionId: section.id } });
  const cols = section.settings?.columns === 3 ? 'md:grid-cols-3' : section.settings?.columns === 2 ? 'md:grid-cols-2' : 'grid-cols-1';
  const usesGrid = !(section.blocks || []).every(isSelfContained) && (section.settings?.columns || 1) > 1;
  return (
    <div ref={setNodeRef} className={usesGrid ? `grid grid-cols-1 ${cols} gap-2 p-2` : ''}>
      {(section.blocks || []).map((block) => (
        <SortableBlock key={block.id} block={block} sectionId={section.id} ctx={ctx}
          selection={selection} setSelection={setSelection}
          onDup={onDupBlock} onDel={onDelBlock} onToggle={onToggleBlock} />
      ))}
      {(section.blocks || []).length === 0 && <div className="text-center text-gray-600 text-xs py-8">Empty section — drag a block here</div>}
    </div>
  );
}

// ---- Sortable block ----
function SortableBlock({ block, sectionId, ctx, selection, setSelection, onDup, onDel, onToggle }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id, data: { type: 'block', sectionId } });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const selected = selection?.kind === 'block' && selection.blockId === block.id;
  const hidden = block.visible === false;

  return (
    <div ref={setNodeRef} style={style} data-testid={`ed-block-${block.id}`}
      onClick={(e) => { e.stopPropagation(); setSelection({ kind: 'block', sectionId, blockId: block.id }); }}
      className={`relative group ${selected ? 'ring-2 ring-red-500' : 'ring-1 ring-transparent hover:ring-slate-600'} ${hidden ? 'opacity-40' : ''}`}>
      {/* Block control bar */}
      <div className={`absolute top-9 right-1 z-30 flex items-center gap-0.5 bg-slate-900/95 border border-slate-700 rounded px-1 py-0.5 ${selected ? 'flex' : 'hidden group-hover:flex'}`}>
        <button {...attributes} {...listeners} data-testid={`ed-block-drag-${block.id}`} className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-white touch-none p-0.5"><GripVertical className="w-3.5 h-3.5" /></button>
        <IconBtn testid={`ed-block-dup-${block.id}`} title="Duplicate" onClick={(e) => { e.stopPropagation(); onDup(sectionId, block.id); }}><Copy className="w-3 h-3" /></IconBtn>
        <IconBtn testid={`ed-block-hide-${block.id}`} title={hidden ? 'Show' : 'Hide'} onClick={(e) => { e.stopPropagation(); onToggle(sectionId, block.id); }}>{hidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}</IconBtn>
        <IconBtn testid={`ed-block-del-${block.id}`} title="Delete" danger onClick={(e) => { e.stopPropagation(); onDel(sectionId, block.id); }}><Trash2 className="w-3 h-3" /></IconBtn>
      </div>
      {/* Preview (non-interactive) */}
      <div className="pointer-events-none select-none">
        <BlockRenderer block={{ ...block, visible: true }} ctx={ctx} />
      </div>
    </div>
  );
}

const IconBtn = ({ children, onClick, title, testid, danger }) => (
  <button type="button" title={title} data-testid={testid} onClick={onClick}
    className={`p-1 rounded hover:bg-slate-800 ${danger ? 'text-red-400 hover:text-red-300' : 'text-gray-300 hover:text-white'}`}>{children}</button>
);
